import React, {FC} from "react"
import {UserSavingsCategories} from "@irati/types"
import {PieChart, Pie, Cell, Tooltip} from "recharts"
import {Body, Label, H4} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {useTopCategories} from "@irati/adminReporting/hooks"
import Card from "@irati/ui/src/Layout/Card"
import mySavingsTracking from "@irati-tracking/my-savings/mySavings"
import {FormattedMessage} from "@irati/utils/lang"
import PropTypes from "prop-types"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {Rectangle, ChartWrapper, Legend, PieChartCardInsidePage, StyledLink} from "./styles"

interface Props {
  categories?: UserSavingsCategories[]
}

interface CustomTooltipProps {
  active?: boolean
  payload?: {payload: {name: string; value: number}}[]
}

const CategoriesChart: FC<Props> = ({categories}, {apps}) => {
  if (!categories?.length) return null

  const theme = useTheme()
  const pieColors = [
    theme.colors.tertiary.j_400,
    theme.colors.tertiary.f_400,
    theme.colors.tertiary.o_400,
    theme.colors.tertiary.g_200,
    theme.colors.tertiary.y_400
  ]
  const othersPieColor = theme.colors.greys.grey20
  const {pieChartData} = useTopCategories(categories, pieColors, othersPieColor)

  if (!pieChartData.length) return null

  const CustomTooltip: FC<CustomTooltipProps> = ({active, payload}) => {
    if (active && payload && payload.length) {
      const total = pieChartData.reduce((acc, pie) => acc + pie.value, 0)
      const {value} = payload[0].payload
      const percentage = ((value / total) * 100).toFixed(2)
      return (
        <Card tiny>
          <Body>{`${percentage}%`}</Body>
        </Card>
      )
    }

    return null
  }

  return (
    <PieChartCardInsidePage>
      <H4 marginBottom={2}>
        <FormattedMessage id="savings.categoriesChart.title" />
      </H4>
      <ChartWrapper>
        <PieChart width={180} height={180}>
          <Pie dataKey="value" data={pieChartData} innerRadius={40} outerRadius={90}>
            {pieChartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>

        <Legend direction="column" justifyContent="center">
          {pieChartData.map((entry, index) => (
            <Flex key={`legend-${index}`} alignItems="center" flex="1 1 50%" marginBottom={1.5}>
              <Rectangle color={entry.color} />
              {entry.url ? (
                <StyledLink
                  small
                  to={`/categories/${entry.url}`}
                  app={apps.PERKS}
                  cta
                  tertiary
                  onClick={() => mySavingsTracking.clickCategory("my_savings", "my_top_categories")}>
                  {entry.name}
                </StyledLink>
              ) : (
                <Label>{entry.name}</Label>
              )}
            </Flex>
          ))}
        </Legend>
      </ChartWrapper>
    </PieChartCardInsidePage>
  )
}

CategoriesChart.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default CategoriesChart
